import { formatMoney, type InvoicingOverviewDto, type MeDto, type PurchaseListDto, type ReceivableDto, type SalesRegisterDto } from '@bqcrm/shared';
import { Stack, Text, Title } from '@mantine/core';
import { IconAlarm, IconCash, IconClipboardCheck, IconFileInvoice, IconReceipt, IconShoppingCart } from '@tabler/icons-react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../../api';
import { plural } from '../../format';
import { can } from '../../auth';
import { Money } from '../../components/Money';
import { StatCard, StatGrid } from '../../components/ui';

const remaining = (r: ReceivableDto) => Math.max(r.amountGr - r.paidGr, 0);
const isOpen = (r: ReceivableDto) => r.status === 'oczekuje' || r.status === 'czesciowo';
const invoicesN = (n: number) => `${n} ${plural(n, 'faktura', 'faktury', 'faktur')}`;

/**
 * Pulpit Finansów (księgowa): co dziś wymaga działania — należności, dokumenty do wystawienia,
 * nieopłacone faktury sprzedaży, faktury zakupu z KSeF do sprawdzenia i zapłaty. Kafelki prowadzą do zakładek.
 */
export function FinanceDashboard({ me, companyId, open, invoicing }: { me: MeDto; companyId: string | null; open: UseQueryResult<ReceivableDto[]>; invoicing: UseQueryResult<InvoicingOverviewDto> }) {
  const sales = can(me, 'faktury.podglad');
  const purchases = can(me, 'zakupy.podglad');
  const c = companyId ? `&companyId=${companyId}` : '';
  const unpaidSales = useQuery({ queryKey: ['sales-register', 'pulpit', companyId], queryFn: () => api<SalesRegisterDto>(`/finance/sales-invoices?docs=ksiegowe&pay=nieoplacone&limit=1${c}`), enabled: sales });
  const buys = useQuery({ queryKey: ['purchases', 'pulpit', companyId], queryFn: () => api<PurchaseListDto>(`/finance/purchases?filter=do_zaplaty&limit=1${c}`), enabled: purchases });

  const rows = (open.data ?? []).filter((r) => isOpen(r) && (!companyId || r.companyId === companyId));
  // QA P27: zaległości z umów niepodpisanych — osobno (należność warunkowa)
  const overdue = rows.filter((r) => r.overdue && r.contractSigned);
  const overdueUnsigned = rows.filter((r) => r.overdue && !r.contractSigned);
  const inv = invoicing.data;
  const inCompany = <T extends { companyId: string }>(x: T[]) => x.filter((r) => !companyId || r.companyId === companyId);
  const toIssue = inv ? inCompany(inv.uninvoicedPayments).length + inCompany(inv.proformaDue).length + inCompany(inv.finalDue).length : 0;
  const lateIssue = inv ? inCompany(inv.uninvoicedPayments).filter((r) => r.overdue).length : 0;
  const link = (v: string) => `/finanse?widok=${v}`;

  return (
    <Stack gap="lg">
      <div>
        <Title order={4} mb="xs">
          Sprzedaż i należności
        </Title>
        <StatGrid minWidth={230}>
          <StatCard label="Należności do zapłaty" value={<Money gr={rows.reduce((a, r) => a + remaining(r), 0)} />} icon={<IconCash size={22} />} hint={`${rows.length} poz. z harmonogramów umów`} to={link('naleznosci')} />
          <StatCard label="Należności po terminie" value={<Money gr={overdue.reduce((a, r) => a + remaining(r), 0)} />} icon={<IconAlarm size={22} />} hint={`${overdue.length} poz.${overdueUnsigned.length ? ` · osobno: ${overdueUnsigned.length} poz. z umów niepodpisanych (${formatMoney(overdueUnsigned.reduce((a, r) => a + remaining(r), 0))})` : ''}`} to={link('naleznosci')} tone={overdue.length ? 'alert' : undefined} />
          {can(me, 'faktury.podglad') && (
            <StatCard label="Do zafakturowania" value={toIssue} icon={<IconFileInvoice size={22} />} hint={lateIssue ? `w tym ${lateIssue} po terminie wystawienia` : 'wpłaty bez faktury, pro formy, faktury końcowe'} to={link('faktury')} tone={lateIssue ? 'alert' : undefined} />
          )}
          {sales && (
            <StatCard
              label="Nieopłacone faktury sprzedaży"
              value={<Money gr={unpaidSales.data?.sums.toPayGr ?? 0} />}
              icon={<IconReceipt size={22} />}
              hint={`${invoicesN(unpaidSales.data?.total ?? 0)} (końcowe, VAT)`}
              to={link('sprzedaz')}
              tone={unpaidSales.data?.sums.overdueGr ? 'alert' : undefined}
            />
          )}
        </StatGrid>
        {sales && !!unpaidSales.data?.sums.overdueGr && (
          <Text size="xs" c="var(--app-text-red)" mt={6}>
            Faktury sprzedaży po terminie: <Money gr={unpaidSales.data.sums.overdueGr} />
          </Text>
        )}
      </div>
      {purchases && (
        <div>
          <Title order={4} mb="xs">
            Zakupy (KSeF)
          </Title>
          <StatGrid minWidth={230}>
            <StatCard label="Faktury do sprawdzenia" value={buys.data?.counts.doSprawdzenia ?? 0} icon={<IconClipboardCheck size={22} />} hint="nowe z KSeF" to={link('zakupy&filtr=do_sprawdzenia')} />
            <StatCard label="Do zapłaty" value={<Money gr={buys.data?.sums.toPayGr ?? 0} />} icon={<IconShoppingCart size={22} />} hint={invoicesN(buys.data?.counts.doZaplaty ?? 0)} to={link('zakupy&filtr=do_zaplaty')} />
            <StatCard label="Zakupy po terminie" value={<Money gr={buys.data?.sums.overdueGr ?? 0} />} icon={<IconAlarm size={22} />} hint={invoicesN(buys.data?.counts.poTerminie ?? 0)} to={link('zakupy&filtr=po_terminie')} tone={buys.data?.counts.poTerminie ? 'alert' : undefined} />
          </StatGrid>
        </div>
      )}
    </Stack>
  );
}
